import React, { useEffect, useState } from 'react'
import { useAppSelector } from '../../../store/hooks'
import { selectAccountId } from '../../../store/auth/authSlice'
import { User } from '../../../models/User'
import { useListTemplatesQuery } from '../../../store/templateLibrary/templateLibrary'
import { useLazyGetUsersQuery } from '../../../store/user/user'
import { CustomTemplateValue, TemplateLookup, TemplateTabContent } from '../../../models/TemplateLibrary'
import { Form, Spinner } from 'react-bootstrap'
import { TemplateTypeBadge } from './TemplateTypeBadge'
import { TemplateScopeBadge } from './TemplateScopeBadge'
import { SystemUserBadge, TemplateCreatedByUserBadge } from './TemplateCreatedByUserBadge'
import { formatDate } from '../../../common/dateUtils'
import { ZOCKS_SYSTEM_USER_ID } from './utils'
import './TemplatesTabView.scss'

export interface TemplatesTabViewProps {
  templatesTab: TemplateTabContent
  selectedTemplateId?: string
  onClick?: (templateLookup: TemplateLookup) => void
}

export const TemplatesTabView: React.FC<TemplatesTabViewProps> = ({ templatesTab, selectedTemplateId, onClick }) => {
  const accountId = useAppSelector(selectAccountId)
  const [templateFilter, setTemplateFilter] = useState<string>('')
  const [usersById, setUsersById] = useState<{ [key: string]: User }>({})
  const { data: templates, isLoading: isTemplatesLoading } = useListTemplatesQuery(
    { accountId, ...templatesTab.searchParams },
    { refetchOnMountOrArgChange: 60 },
  )

  const [triggerGetUsers] = useLazyGetUsersQuery()

  const handleTemplateFilterChange = async (filter: string) => {
    setTemplateFilter(filter)
  }

  const templateFilterFunction = (template: TemplateLookup) => {
    if (!templateFilter) {
      return true
    }
    return template.name.toLowerCase().includes(templateFilter.toLowerCase()) || template.description.toLowerCase().includes(templateFilter.toLowerCase())
  }

  useEffect(() => {
    const updateUserNamesById = async () => {
      if (!templates) {
        return
      }

      try {
        const response = await triggerGetUsers({
          accountId,
          ids: [templates.data.filter((template) => 'createdBy' in template).map((template) => (template as CustomTemplateValue).createdBy)].flat(),
          identityTypes: ['idp'],
          pageRequest: { size: 500, page: 0 },
        }).unwrap()
        if (!response?.data) {
          return
        }
        setUsersById(
          response.data.reduce((acc: { [key: string]: User }, user: User) => {
            acc[user.id] = user
            return acc
          }, {}),
        )
      } catch (e) {
        console.error(e)
      }
    }
    updateUserNamesById()
  }, [templates, accountId, triggerGetUsers])

  return (
    <div>
      <div className='d-flex justify-content-between align-items-center gap-3'>
        <div>
          <h4 className='mb-0'>{templatesTab.header}</h4>
          <div className='paragraph-medium'>{templatesTab.subHeader}</div>
        </div>
        <div className='template-search-control d-flex align-items-center gap-2 border rounded zocks-border-color-gray-500 px-2 flex-grow-1'>
          <i className='bi bi-search label-large' />
          <Form.Control
            type='text'
            list='rooms'
            value={templateFilter}
            onChange={(e) => handleTemplateFilterChange(e.currentTarget.value)}
            placeholder={`Search ${templatesTab.label}`}
            className='border-0 px-0'
          />
        </div>
      </div>
      {isTemplatesLoading && (
        <div className='d-flex justify-content-center align-items-center h-100 pt-5'>
          <Spinner />
        </div>
      )}
      {templates && templates.data.length === 0 && <div className='mt-4'>No templates available in this category.</div>}
      {templates && templates.data.length > 0 && (
        <div className='templates-list row row-cols-1 row-cols-md-2 g-3 mt-3'>
          {templates.data.filter(templateFilterFunction).map((template) => (
            <TemplateView
              key={template.id}
              templateLookup={template}
              showTemplateType={templatesTab.showTemplateType}
              createdByUser={'createdBy' in template ? usersById[(template as CustomTemplateValue).createdBy] : undefined}
              selected={template.id === selectedTemplateId}
              onClick={onClick}
            />
          ))}
        </div>
      )}
    </div>
  )
}

interface TemplateViewProps {
  templateLookup: TemplateLookup
  showTemplateType: boolean
  createdByUser?: User
  selected?: boolean
  onClick?: (templateLookup: TemplateLookup) => void
}

const TemplateView: React.FC<TemplateViewProps> = ({ templateLookup, showTemplateType, createdByUser, selected = false, onClick }) => {
  const handleTemplateClick = () => {
    if (onClick) {
      onClick(templateLookup)
    }
  }

  return (
    <div key={templateLookup.id} className='col'>
      <div
        className={`d-flex flex-column template-item ${selected ? 'template-item-selected' : ''} p-3 h-100 cursor-pointer`}
        onClick={handleTemplateClick}
        role='button'
        tabIndex={0}
      >
        <div className='d-flex justify-content-start align-items-center gap-2 pb-1 paragraph-small'>
          {showTemplateType ? <TemplateTypeBadge templateValue={templateLookup} /> : null}
          <TemplateScopeBadge scope={templateLookup.scope} />
        </div>
        <h6 className='template-name paragraph-large fw-bold pt-1'>{templateLookup.name}</h6>
        <p className='paragraph-small zocks-text-gray-700 pt-2'>{templateLookup.description}</p>
        <div className='d-flex justify-content-start align-items-center gap-3 paragraph-extra-small mt-auto'>
          {createdByUser ? (
            <TemplateCreatedByUserBadge createdByUser={createdByUser} />
          ) : templateLookup.scope === 'system' || ('createdBy' in templateLookup && templateLookup.createdBy === ZOCKS_SYSTEM_USER_ID) ? (
            <SystemUserBadge />
          ) : null}
          {'updatedAt' in templateLookup ? (
            <div className='d-flex align-items-center gap-1'>
              <i className='bi bi-calendar' />
              <p className='mb-0'>Updated {formatDate(new Date(templateLookup.updatedAt))}</p>
            </div>
          ) : null}
        </div>
      </div>
    </div>
  )
}
